'use client';

import { c as _c } from "react/compiler-runtime";
import { ChallengeStatus, SERVER_CHALLENGE_SNAPSHOT, getDocumentChallengeState } from '@spa/lib/fastly-challenge/challengeState';
import PopupComponent, { PopupComponentRef, PopupType } from '@spa/components/popup/popup.component';
import { ReactNode, useEffect, useId, useRef, useSyncExternalStore } from 'react';
import ButtonComponent from '@spa/components/button/button/button.component';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import FastlyChallengeSyncComponent from './fastly-challenge-sync.component';
import IntlProvider from '@spa/lib/i18n/client/components/intl.provider';
import { TranslationsByIdByNamespace } from '@spa/lib/i18n/common/TranslationsByIdByNamespace';
import { createPortal } from 'react-dom';
import styles from './fastly-challenge.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export interface Props {
  readonly children: ReactNode;
  readonly enabled: boolean;
  readonly translationsByIdByNamespace: TranslationsByIdByNamespace;
}
const subscribe = (listener: () => void): (() => void) => getDocumentChallengeState().subscribe(listener);
const getSnapshot = (): typeof SERVER_CHALLENGE_SNAPSHOT => getDocumentChallengeState().getSnapshot();
const getServerSnapshot = (): typeof SERVER_CHALLENGE_SNAPSHOT => SERVER_CHALLENGE_SNAPSHOT;
function ChallengeEpisodePopup(t0) {
  const $ = _c(22);
  const {
    episodeId,
    sticky
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("fastly-challenge");
  const titleId = useId();
  const popupRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      if (event.key === "Escape") {
        event.stopPropagation();
        popupRef.current?.close();
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== episodeId) {
    t3 = () => getDocumentChallengeState().dismissPopup(episodeId);
    $[2] = episodeId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== formatMessage || $[5] !== sticky) {
    t4 = sticky ? <ButtonComponent buttonType={ButtonType.PRIMARY} text={formatMessage("reload")} onClick={_temp2} /> : undefined;
    $[4] = formatMessage;
    $[5] = sticky;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== formatMessage) {
    t5 = formatMessage("title");
    $[7] = formatMessage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5 || $[10] !== titleId) {
    t6 = <h2 id={titleId}>{t5}</h2>;
    $[9] = t5;
    $[10] = titleId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== formatMessage) {
    t7 = formatMessage("description");
    $[12] = formatMessage;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <p>{t7}</p>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t6 || $[19] !== t8 || $[20] !== titleId) {
    t9 = <div onKeyDownCapture={t2}><PopupComponent ref={popupRef} isOpen={true} onClosed={t3} popupType={PopupType.CENTER} focusDialogOnOpen={true} ariaLabelledBy={titleId} dialogClassName={styles.dialog} overlayClassName={styles.overlay} footerContent={t4}>{t6}{t8}</PopupComponent></div>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
    $[19] = t8;
    $[20] = titleId;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp2() {
  return window.location.reload();
}
function _temp() {
  const previousActiveElement = document.activeElement;
  return () => {
    if (previousActiveElement instanceof HTMLElement && previousActiveElement.isConnected) {
      previousActiveElement.focus({
        preventScroll: true
      });
    }
  };
}
function ChallengePopup() {
  const $ = _c(10);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  let t0;
  if ($[0] !== snapshot.integrationFailure || $[1] !== snapshot.status) {
    t0 = snapshot.status === ChallengeStatus.CAPTCHA_PROMPTED && snapshot.integrationFailure === null && createPortal(<div className={styles.captchaBackdrop} aria-hidden="true" data-testid="fastly-captcha-backdrop" />, document.body);
    $[0] = snapshot.integrationFailure;
    $[1] = snapshot.status;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== snapshot.episodeId || $[4] !== snapshot.integrationFailure || $[5] !== snapshot.popupOpen) {
    t1 = snapshot.episodeId !== null && snapshot.popupOpen && <ChallengeEpisodePopup key={snapshot.episodeId} episodeId={snapshot.episodeId} sticky={snapshot.integrationFailure !== null} />;
    $[3] = snapshot.episodeId;
    $[4] = snapshot.integrationFailure;
    $[5] = snapshot.popupOpen;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export default function FastlyChallengeComponent(t0) {
  const $ = _c(8);
  const {
    children,
    enabled,
    translationsByIdByNamespace
  } = t0;
  let documentEnabled = enabled;
  if (typeof document !== "undefined") {
    let t1;
    if ($[0] !== enabled) {
      const state = getDocumentChallengeState();
      state.initialize(enabled);
      t1 = state.getSnapshot();
      $[0] = enabled;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    documentEnabled = t1.enabled === true;
  }
  let t1;
  if ($[2] !== documentEnabled || $[3] !== translationsByIdByNamespace) {
    t1 = documentEnabled && <IntlProvider translationsByIdByNamespace={translationsByIdByNamespace}><FastlyChallengeSyncComponent /><ChallengePopup /></IntlProvider>;
    $[2] = documentEnabled;
    $[3] = translationsByIdByNamespace;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== children || $[6] !== t1) {
    t2 = <>{children}{t1}</>;
    $[5] = children;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}